import { motion } from 'framer-motion'
import { Eye, EyeOff, Lock, User } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Input } from './ui/input'
import { secureFetch } from '../lib/csrf'
import logger from '../lib/logger'

const Login = ({ onLogin }) => {
    const [showPassword, setShowPassword] = useState(false)
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    })
    const [errors, setErrors] = useState({})

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
        // 清除該欄位的錯誤
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }))
        }
    }

    const validateForm = () => {
        const newErrors = {}

        if (!formData.username.trim()) {
            newErrors.username = '請輸入用戶名'
        }

        if (!formData.password) {
            newErrors.password = '請輸入密碼'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        setLoading(true)
        logger.info('用戶嘗試登入', { username: formData.username })

        try {
            const response = await secureFetch('/api/user/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: formData.username,
                    password: formData.password
                })
            })

            const data = await response.json()

            if (data.status === 'success') {
                logger.info('登入成功', { username: formData.username })
                onLogin(data.data)
            } else {
                logger.warn('登入失敗', { message: data.message })
                alert(data.message || '登入失敗')
            }
        } catch (error) {
            logger.error('登入請求失敗', error)
            alert('網路連接失敗，請稍後再試')
        } finally {
            setLoading(false)
        }
    }

    const [realUsers, setRealUsers] = useState([])
    const [loadingUsers, setLoadingUsers] = useState(true)

    // 載入實際用戶列表供快速登錄使用
    useEffect(() => {
        fetchRealUsers()
    }, [])

    const fetchRealUsers = async () => {
        try {
            const response = await fetch('/api/user/demo-users')

            if (!response.ok) {
                logger.warn('無法載入測試帳號', { status: response.status })
                return
            }

            const data = await response.json()
            if (data.status === 'success') {
                setRealUsers(data.users)
            } else {
                console.error('載入用戶列表失敗:', data.message)
            }
        } catch (error) {
            console.error('獲取用戶列表失敗:', error)
        } finally {
            setLoadingUsers(false)
        }
    }

    const quickLogin = (username, password) => {
        setFormData({
            username,
            password
        })
        // 自動提交登入表單
        setTimeout(() => {
            document.querySelector('form').dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
        }, 100)
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <Card className="shadow-2xl">
                    <CardHeader className="text-center pb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                            <span className="text-white font-bold text-2xl">IP</span>
                        </div>
                        <CardTitle className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            智財權AI助理平台
                        </CardTitle>
                        <CardDescription>
                            請登入您的帳戶
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-6">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <label htmlFor="username" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    用戶名 <span className="text-gray-500">(或電子郵件)</span>
                                </label>
                                <div className="relative">
                                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <Input
                                        id="username"
                                        name="username"
                                        type="text"
                                        placeholder="輸入用戶名"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                        className={`pl-10 ${errors.username ? 'border-red-500' : ''}`}
                                    />
                                </div>
                                {errors.username && (
                                    <p className="text-sm text-red-600">{errors.username}</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    密碼
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <Input
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="輸入密碼"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        className={`pl-10 pr-10 ${errors.password ? 'border-red-500' : ''}`}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="absolute right-0 top-0 h-full px-3"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="h-4 w-4 text-gray-400" />
                                        ) : (
                                            <Eye className="h-4 w-4 text-gray-400" />
                                        )}
                                    </Button>
                                </div>
                                {errors.password && (
                                    <p className="text-sm text-red-600">{errors.password}</p>
                                )}
                            </div>

                            <Button
                                type="submit"
                                className="w-full"
                                disabled={loading}
                            >
                                {loading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                ) : null}
                                登入
                            </Button>
                        </form>

                        {/* 快速登錄帳戶 */}
                        <div className="border-t pt-4">
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 text-center">
                                快速登入 ({loadingUsers ? '載入中...' : `${realUsers.length} 個實際帳戶`})
                            </p>
                            <div className="grid grid-cols-1 gap-2">
                                {loadingUsers ? (
                                    <div className="text-center py-4">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                                        <p className="text-xs text-gray-500 mt-1">載入測試帳號中...</p>
                                    </div>
                                ) : realUsers.length > 0 ? (
                                    realUsers.map((user, index) => (
                                        <Button
                                            key={index}
                                            variant="outline"
                                            size="sm"
                                            className="justify-start text-left h-auto py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                            onClick={() => quickLogin(user.username, user.password)}
                                        >
                                            <div className="flex flex-col items-start w-full">
                                                <div className="font-medium text-sm">{user.username}</div>
                                                <div className="text-xs text-gray-500 mt-0.5">{user.fullName} - {user.role}</div>
                                                <div className="text-xs text-blue-600 mt-0.5">密碼: {user.password}</div>
                                            </div>
                                        </Button>
                                    ))
                                ) : (
                                    <div className="text-center text-sm text-gray-500 py-2">
                                        <p>沒有可用的快速登入帳戶</p>
                                        <p className="text-xs mt-1">如果你看到這個，請檢查後端API</p>
                                    </div>
                                )}
                            </div>

                            {/* 新用戶說明 */}
                            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                <p className="text-xs text-blue-700 dark:text-blue-300 text-center">
                                    💡 新用戶帳戶僅能由管理員創建<br />
                                    請聯繫系統管理員申請帳戶
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    )
}

export default Login
